import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Calendar, Menu, X, ChevronDown, Clock } from 'lucide-react';
import { siteSettings as fallbackSettings, navItems, logo } from '../data/mock';
import { useContent } from '../context/ContentContext';
import { Button } from './ui/button';
import AppointmentModal from './AppointmentModal';

const Header = () => {
  const { siteSettings = fallbackSettings, serviceAreas = [] } = useContent();
  const navItemsComputed = navItems.map((it) =>
    it.label === 'Service Areas'
      ? {
          ...it,
          path: serviceAreas[0] ? `/service-areas/${serviceAreas[0].slug}` : it.path,
          children: (serviceAreas.length ? serviceAreas : (it.children || [])).map((a) =>
            a.slug
              ? { label: a.state ? `${a.city}, ${a.state}` : a.city, path: `/service-areas/${a.slug}` }
              : a
          )
        }
      : it
  );
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top strip */}
      <div className="bg-brand-green text-white text-sm hidden md:block">
        <div className="container-c flex items-center justify-between h-10">
          <div className="flex items-center gap-2 font-medium">
            <Clock size={15} />
            <span>{siteSettings.emergencyNote}</span>
          </div>
          <div className="flex items-center gap-6">
            <span>{siteSettings.address}, {siteSettings.city}</span>
            <a href={`mailto:${siteSettings.email}`} className="hover:underline">{siteSettings.email}</a>
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? 'bg-brand-bluedeep shadow-xl' : 'bg-brand-bluedeep/95'}`}>
        <div className="container-c">
          <div className="flex items-center justify-between gap-3 h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0 group">
              <div className="bg-white rounded-lg px-3 py-2 shadow-md group-hover:shadow-lg transition-shadow">
                <img src={logo} alt="Comfort Air Mechanical" className="h-9 w-auto" />
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden xl:flex items-center gap-0.5 flex-1 justify-center">
              {navItemsComputed.map((item) => (
                <div key={item.label} className="relative"
                  onMouseEnter={() => setOpenDrop(item.label)}
                  onMouseLeave={() => setOpenDrop(null)}>
                  <Link to={item.path}
                    className={`px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-1 whitespace-nowrap transition-colors ${isActive(item.path) ? 'text-brand-green' : 'text-white/90 hover:text-brand-green'}`}>
                    {item.label}
                    {item.children && <ChevronDown size={14} className={`transition-transform ${openDrop === item.label ? 'rotate-180' : ''}`} />}
                  </Link>
                  {item.children && openDrop === item.label && (
                    <div className="absolute left-0 top-full pt-2 w-56 z-50">
                      <div className="bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden py-2 animate-fade-up max-h-[70vh] overflow-y-auto">
                        {item.children.map((c) => (
                          <Link key={c.path} to={c.path}
                            className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-brand-bluelight hover:text-brand-blue transition-colors">
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* CTAs */}
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <a href={`tel:${siteSettings.phoneRaw}`} className="hidden 2xl:block">
                <Button variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white font-semibold gap-2 whitespace-nowrap">
                  <Phone size={16} /> {siteSettings.phone}
                </Button>
              </a>
              <Button onClick={() => setApptOpen(true)} className="bg-brand-green hover:bg-brand-greendark text-white font-semibold gap-2 shadow-lg whitespace-nowrap">
                <Calendar size={16} /> <span className="hidden lg:inline">Request Appointment</span><span className="lg:hidden">Appointment</span>
              </Button>
            </div>

            {/* Mobile toggle */}
            <button className="xl:hidden text-white p-2 shrink-0" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="xl:hidden bg-brand-bluedeep border-t border-white/10 animate-fade-up max-h-[80vh] overflow-y-auto">
            <div className="container-c py-4 flex flex-col gap-1">
              {navItemsComputed.map((item) => (
                <div key={item.label}>
                  <Link to={item.path} className="block py-2.5 text-white font-semibold">{item.label}</Link>
                  {item.children && (
                    <div className="pl-4 flex flex-col">
                      {item.children.map((c) => (
                        <Link key={c.path} to={c.path} className="py-2 text-white/70 text-sm">{c.label}</Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex flex-col gap-2 pt-3">
                <a href={`tel:${siteSettings.phoneRaw}`}>
                  <Button variant="outline" className="w-full border-white/30 bg-white/5 text-white gap-2"><Phone size={16} /> {siteSettings.phone}</Button>
                </a>
                <Button onClick={() => { setApptOpen(true); setMobileOpen(false); }} className="w-full bg-brand-green hover:bg-brand-greendark gap-2"><Calendar size={16} /> Request Appointment</Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <AppointmentModal open={apptOpen} onClose={() => setApptOpen(false)} />
    </>
  );
};

export default Header;
